import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { FREE_WIL_MESSAGE_LIMIT, isWilMessageLimitReached, remainingWilMessages, useAdvisorsAccess } from '@src/components/sco/Advisors/model/access';
import { gql } from '@src/lib/gql';

/**
 * Server-backed Wil free-tier usage (ADV-A3).
 * Counts human messages across advisor-wil sessions on the backend —
 * no client overfetch or extrapolation.
 */
const WIL_MESSAGE_USAGE_QUERY = gql`
  query AdvisorWilMessageUsage {
    currentUser {
      id
      userId
      advisorWilMessageUsage {
        usedMessages
        limit
        remaining
        isLimitReached
        sessionCount
        isLimited
      }
    }
  }
`;

/**
 * Free-tier Wil message meter for Ask / paywall UI.
 * Unlimited tiers skip the server query; free registered users load exact usage.
 * Return shape matches the previous client-count hook so AskSection / HomeSection keep working.
 */
export function useWilMessageUsage() {
  const $ = _c(21);
  const access = useAdvisorsAccess();
  const skip = !access.isWilMessageLimited;
  let t0;
  if ($[0] !== skip) {
    t0 = {
      skip,
      fetchPolicy: "cache-and-network"
    };
    $[0] = skip;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const usageQuery = useQuery(WIL_MESSAGE_USAGE_QUERY, t0);
  const usage = usageQuery.data?.currentUser?.advisorWilMessageUsage;
  const usedMessages = skip || access.canAccessUnlimitedWilAi ? 0 : usage?.usedMessages ?? 0;
  const limit = typeof usage?.limit === "number" ? usage.limit : FREE_WIL_MESSAGE_LIMIT;
  let t1;
  if ($[2] !== limit || $[3] !== usage || $[4] !== usedMessages) {
    t1 = typeof usage?.remaining === "number" ? usage.remaining : remainingWilMessages(usedMessages, limit);
    $[2] = limit;
    $[3] = usage;
    $[4] = usedMessages;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const remaining = t1;
  const sessionCount = usage?.sessionCount ?? 0;
  let t2;
  if ($[6] !== access.isWilMessageLimited || $[7] !== limit || $[8] !== usage || $[9] !== usedMessages) {
    t2 = access.isWilMessageLimited && (typeof usage?.isLimitReached === "boolean" ? usage.isLimitReached : isWilMessageLimitReached(usedMessages, limit));
    $[6] = access.isWilMessageLimited;
    $[7] = limit;
    $[8] = usage;
    $[9] = usedMessages;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const isLimitReached = t2;
  const hasUsageData = !!usageQuery.data?.currentUser;
  const loading = !skip && !hasUsageData && usageQuery.loading;
  let t3;
  if ($[11] !== access || $[12] !== isLimitReached || $[13] !== limit || $[14] !== loading || $[15] !== remaining || $[16] !== sessionCount || $[17] !== usageQuery.error || $[18] !== usageQuery.refetch || $[19] !== usedMessages) {
    t3 = {
      ...access,
      loading,
      error: usageQuery.error,
      usedMessages,
      remainingMessages: remaining,
      sessionCount,
      limit,
      isLimitReached,
      refetch: usageQuery.refetch
    };
    $[11] = access;
    $[12] = isLimitReached;
    $[13] = limit;
    $[14] = loading;
    $[15] = remaining;
    $[16] = sessionCount;
    $[17] = usageQuery.error;
    $[18] = usageQuery.refetch;
    $[19] = usedMessages;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  return t3;
}